/**
 * Auto-advance for the product recommendations carousels
 * ("You may also like", "Styled With").
 *
 * WHY NOT THE THEME'S OWN AUTOPLAY
 *
 * <slideshow-component> supports autoplay via an `autoplay="seconds"` attribute,
 * and using it would be less code. But its next() advances by
 * visibleSlides.length — a whole page. With four cards visible on desktop that
 * jumps four products at a time, which is not what was asked for. So this steps
 * the carousel one index at a time instead.
 *
 * TWO WAYS TO STEP, AND WHY
 *
 * The preferred path is the component's public select(), so the theme keeps
 * ownership of snapping, aria state and lazy-loading. But select() bails early
 * in several internal cases (mid-transition, no scroller yet, a disabled or
 * nested slideshow), and when it bails it does so silently — which is exactly
 * what "it works on desktop but not on mobile" looks like.
 *
 * So advance() verifies: it remembers the index, calls select(), and if the
 * index has not moved shortly after, it scrolls the track by one slide itself.
 * The fallback is plain scrollTo on a scroll-snap container, which is what the
 * component does under the hood anyway. Belt and braces beats another round of
 * guessing at which internal guard fired.
 *
 * Looping depends on the `infinite` attribute, which
 * snippets/resource-list-carousel.liquid sets for recommendation carousels;
 * without it select() clamps at the last slide and the row would stop there.
 *
 * Does nothing unless the section is set to Carousel layout. Pauses on hover,
 * focus and touch, when the tab is hidden, when the carousel is off screen, and
 * for visitors who prefer reduced motion — a row that moves as someone reaches
 * for it costs a sale.
 */

const TSC_REC_INTERVAL = 2000;
const TSC_REC_MANUAL_PAUSE_MS = 8000;
const TSC_REC_VERIFY_MS = 400;

class TscRecAutoplay {
  constructor(slideshow) {
    this.slideshow = slideshow;
    this.timer = null;
    this.manualPause = null;
    this.interacting = false;
    this.inView = false;

    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (this.motion.matches) return;

    this.bind();
    this.observe();
  }

  track() {
    return this.slideshow.querySelector('slideshow-slides');
  }

  slides() {
    return Array.from(this.slideshow.querySelectorAll('slideshow-slide'));
  }

  bind() {
    ['mouseenter', 'focusin', 'touchstart', 'pointerdown'].forEach((evt) =>
      this.slideshow.addEventListener(evt, () => this.hold(true), { passive: true })
    );
    ['mouseleave', 'focusout', 'touchend', 'touchcancel'].forEach((evt) =>
      this.slideshow.addEventListener(evt, () => this.hold(false), { passive: true })
    );

    /* A click on the theme's own arrows is a person taking over. */
    this.slideshow.addEventListener('click', (event) => {
      const control =
        event.target instanceof HTMLElement ? event.target.closest('.slideshow-control') : null;
      if (control) this.holdManually();
    });

    this.onVisibility = () => this.evaluate();
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  observe() {
    if (!('IntersectionObserver' in window)) {
      this.inView = true;
      this.evaluate();
      return;
    }

    this.io = new IntersectionObserver(
      (entries) => {
        this.inView = entries[0].isIntersecting;
        this.evaluate();
      },
      { threshold: 0.15 }
    );
    this.io.observe(this.slideshow);
  }

  hold(state) {
    this.interacting = state;
    this.evaluate();
  }

  holdManually() {
    this.stop();
    window.clearTimeout(this.manualPause);
    this.manualPause = window.setTimeout(() => {
      this.manualPause = null;
      this.evaluate();
    }, TSC_REC_MANUAL_PAUSE_MS);
  }

  /* Measured, not offsetParent: a laid-out element can report a null
     offsetParent in some containing blocks while being perfectly visible. */
  isHidden() {
    if (this.slideshow.closest('[hidden]')) return true;
    const box = this.slideshow.getBoundingClientRect();
    return box.width === 0 || box.height === 0;
  }

  evaluate() {
    const blocked =
      this.interacting ||
      this.manualPause !== null ||
      document.hidden ||
      !this.inView ||
      this.isHidden() ||
      this.motion.matches ||
      this.slides().length < 2;

    if (blocked) this.stop();
    else this.start();
  }

  start() {
    if (this.timer) return;
    this.timer = window.setInterval(() => this.advance(), TSC_REC_INTERVAL);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  index() {
    const value = this.slideshow.current;
    return typeof value === 'number' ? value : this.scrollIndex();
  }

  /* Index derived from scroll position, for the fallback path. */
  scrollIndex() {
    const track = this.track();
    if (!track) return 0;

    let best = 0;
    let min = Infinity;
    this.slides().forEach((slide, i) => {
      const d = Math.abs(slide.offsetLeft - track.offsetLeft - track.scrollLeft);
      if (d < min) {
        min = d;
        best = i;
      }
    });
    return best;
  }

  advance() {
    const before = this.index();

    if (typeof this.slideshow.select === 'function') {
      this.slideshow.select(before + 1);
    }

    /* If the component declined to move, do it directly. */
    window.setTimeout(() => {
      if (this.index() !== before) return;
      this.scrollStep(before);
    }, TSC_REC_VERIFY_MS);
  }

  scrollStep(from) {
    const track = this.track();
    const slides = this.slides();
    if (!track || slides.length < 2) return;

    const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
    const target = atEnd ? slides[0] : slides[Math.min(from + 1, slides.length - 1)];
    if (!target) return;

    track.scrollTo({
      left: target.offsetLeft - track.offsetLeft,
      behavior: 'smooth',
    });
  }
}

function initTscRecCarousels() {
  document
    .querySelectorAll('product-recommendations slideshow-component')
    .forEach((slideshow) => {
      if (slideshow.dataset.tscRecBound === 'true') return;
      slideshow.dataset.tscRecBound = 'true';

      /* The custom element may not have upgraded yet when this runs. */
      const startWhenReady = () => new TscRecAutoplay(slideshow);
      if (typeof slideshow.select === 'function') {
        startWhenReady();
      } else if (window.customElements) {
        window.customElements
          .whenDefined('slideshow-component')
          .then(startWhenReady)
          .catch(startWhenReady);
      } else {
        startWhenReady();
      }
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTscRecCarousels);
} else {
  initTscRecCarousels();
}

/* Recommendations arrive over the network after first paint, and the theme
   editor re-renders sections in place — so watch for both. */
document.addEventListener('shopify:section:load', initTscRecCarousels);

if ('MutationObserver' in window) {
  const observer = new MutationObserver(() => initTscRecCarousels());
  const start = () => {
    document.querySelectorAll('product-recommendations').forEach((host) => {
      observer.observe(host, { childList: true, subtree: true });
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}
